import {getSequenceStandardizedMedicines} from '@/modules/patients-therapies/functions'
import {Span, Table, Text} from '@chakra-ui/react'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {getShortDateFormatter, joinLabels, multipleDigitsNumberFormatter, VERTICALLY_ALIGNED_DOT} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {StatusBadge} from './status-badge'

// structural view of a prescription — full sequences (PatientTherapySequencePopulated)
// and the lighter rows from getPatientTherapies (PatientTherapy) both satisfy it
interface PrescriptionSequenceLike {
  id: string
  therapyDate: number
  time: string
  status?: string | null
  applicationForm?: {name?: string | null} | null
  carrierSolution?: {name?: string | null} | null
  volume?: number | null
  volumeMode?: string | null
  medicine?: readonly {
    totalAmount?: number | null
    activeSubstance?: {name?: string | null} | null
    prescribedName?: {name?: string | null} | null
  }[]
}

interface PrescriptionTableRowProps {
  sequence: PrescriptionSequenceLike
  isCurrent?: boolean
  onClick?: () => void
}

export function PrescriptionTableRow({sequence, isCurrent, onClick}: PrescriptionTableRowProps) {
  const {t} = useTranslate('common')
  const medicines = getSequenceStandardizedMedicines(sequence as PatientTherapySequence)
  const carrierLabel = sequence.carrierSolution?.name
    ? joinLabels([
        `${sequence.volumeMode} ${sequence.carrierSolution.name}`,
        sequence.volume ? `${multipleDigitsNumberFormatter.format(sequence.volume)} ml` : null,
      ])
    : ''

  const therapyDate = new Date(sequence.therapyDate)
  const isToday = therapyDate.toDateString() === new Date().toDateString()

  // keep text selection possible — clicking without a selection navigates to the prescription
  function onRowClick() {
    const selectedText = window.getSelection()?.toString()
    if (selectedText?.length) return
    onClick?.()
  }

  return (
    <Table.Row
      bg={isCurrent ? 'bg.info' : undefined}
      color={isCurrent ? 'info.fg' : undefined}
      onClick={onClick ? onRowClick : undefined}
      role={onClick ? 'button' : undefined}
    >
      <Table.Cell paddingX="3" paddingY="2" whiteSpace="nowrap">
        {isToday ? t('today') : getShortDateFormatter().format(therapyDate)}
      </Table.Cell>
      <Table.Cell paddingX="3" paddingY="2">
        <Text whiteSpace="wrap">
          <Span fontWeight="medium">
            {joinLabels(
              medicines.map(m =>
                `${m.name} · ${multipleDigitsNumberFormatter.format(m.totalAmount ?? 0)} ${m.unit?.ucumCode ?? ''}`.trim()
              )
            ) || '—'}
          </Span>
          {carrierLabel && (
            <Span color="fg.muted">
              {` ${VERTICALLY_ALIGNED_DOT} `}
              {carrierLabel}
            </Span>
          )}
        </Text>
      </Table.Cell>
      <Table.Cell paddingX="3" paddingY="2" color="fg.muted">
        {sequence.applicationForm?.name || '—'}
      </Table.Cell>
      <Table.Cell paddingX="3" paddingY="2">
        <StatusBadge sequence={sequence as PatientTherapySequence} />
      </Table.Cell>
    </Table.Row>
  )
}

export function PrescriptionTableHeader() {
  const {t} = useTranslate('common')

  return (
    <Table.Header>
      <Table.Row color="fg.muted">
        <Table.ColumnHeader textStyle="xs">{t('date')}</Table.ColumnHeader>
        <Table.ColumnHeader textStyle="xs">{t('substance_dose')}</Table.ColumnHeader>
        <Table.ColumnHeader textStyle="xs" whiteSpace="nowrap">
          {t('application_form')}
        </Table.ColumnHeader>
        <Table.ColumnHeader textStyle="xs">{t('status')}</Table.ColumnHeader>
      </Table.Row>
    </Table.Header>
  )
}
